/**
 * BottomNav - Premium bottom navigation bar
 * v0 design: 4 tabs with highlighted center "List App" tab
 */

import { hapticFeedback } from '../lib/telegram';

export type NavTab = 'home' | 'submit' | 'referrals' | 'leaderboard';

interface BottomNavProps {
  active: NavTab;
  onChange: (tab: NavTab) => void;
}

const tabs: { id: NavTab; label: string; icon: (active: boolean) => JSX.Element; highlighted?: boolean }[] = [
  {
    id: 'home',
    label: 'Discover',
    icon: (active) => (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={active ? 2.5 : 2}>
        <circle cx="12" cy="12" r="10" />
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    id: 'submit',
    label: 'List App',
    highlighted: true,
    icon: () => (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
      </svg>
    ),
  },
  {
    id: 'referrals',
    label: 'Referrals',
    icon: (active) => (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={active ? 2.5 : 2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    id: 'leaderboard',
    label: 'Leaderboard',
    icon: (active) => (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={active ? 2.5 : 2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8m-4-4v4m-3.5-9.5L12 8l3.5 3.5M6.5 11.5C4.5 11.5 3 10 3 8V6h4m11 5.5c2 0 3.5-1.5 3.5-3.5V6h-4M7 6h10V4H7v2z" />
      </svg>
    ),
  },
];

export function BottomNav({ active, onChange }: BottomNavProps) {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-[#242424]/95 backdrop-blur-lg border-t border-white/5">
      <div className="max-w-[390px] mx-auto flex items-center justify-around h-20 pb-[max(0rem,env(safe-area-inset-bottom))]">
        {tabs.map((tab) => {
          const isActive = active === tab.id;
          const isHighlighted = tab.highlighted;

          return (
            <button
              key={tab.id}
              onClick={() => {
                hapticFeedback('light');
                onChange(tab.id);
              }}
              className={`
                flex flex-col items-center justify-center gap-1 min-w-[60px] relative
                transition-all active:scale-95
                ${isActive ? 'text-[#0098EA]' : 'text-[#a0a0a0]'}
              `}
            >
              {/* Icon Container */}
              <div
                className={`
                  flex items-center justify-center w-8 h-8 rounded-xl transition-colors
                  ${isHighlighted
                    ? 'bg-[#0098EA] text-white shadow-lg shadow-[#0098EA]/25 w-11 h-11'
                    : isActive
                      ? 'bg-[#0098EA]/10'
                      : 'hover:bg-white/5'
                  }
                `}
              >
                {tab.icon(isActive)}
              </div>

              {/* Label */}
              <span className="text-[10px] font-medium">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
